import type { TFunction } from 'i18next'

import { DepositInvoiceAmountType } from '@features/invoices/enums/depositInvoiceAmountType'
import { InvoiceLineType } from '@features/invoices/enums/invoiceLineType'
import type { InvoiceFormDepositLine } from '@features/invoices/types/invoiceForm/invoiceFormLine/invoiceFormDepositLine'
import { Quote } from '@features/invoices/types/quotes/quote'
import { calculateDepositInvoiceRemaining } from '@features/invoices/utils/depositInvoices/calculateDepositInvoiceRemaining'
import { formatDepositCurrencyAmount } from '@features/invoices/utils/depositInvoices/formatDepositCurrencyAmount'

interface GetDepositLinesFromRowLinesOptions {
  depositValue: number
  depositValueType: InvoiceFormDepositLine['depositValueType']
  quote: Quote
  t: TFunction
}

export const getDepositLinesFromRowLines = ({
  depositValue,
  depositValueType,
  quote,
  t,
}: GetDepositLinesFromRowLinesOptions): InvoiceFormDepositLine[] => {
  const vatCalculated = calculateDepositInvoiceRemaining({
    currency: quote.summary.foreignCurrency,
    depositAmount: depositValue,
    depositType: depositValueType,
    quoteAmount: quote.summary.foreignNetTotal,
    quoteLines: quote.lines,
  })

  const description =
    depositValueType === DepositInvoiceAmountType.Fixed
      ? t('external_invoices.editor.deposit_line_description.fixed', {
          amount: formatDepositCurrencyAmount(depositValue, quote?.summary.foreignCurrency, quote.locale),
          quoteNumber: quote?.estimateNumber,
        })
      : t('external_invoices.editor.deposit_line_description.percentage', {
          percentage: depositValue,
          quoteNumber: quote?.estimateNumber,
        })

  const depositLines = vatCalculated.vatLines.map(
    (vatLine): InvoiceFormDepositLine => ({
      depositValue: vatLine.depositAmountForVatItem,
      depositValueType,
      description,
      totalExcludingVat: vatLine.depositAmountForVatItem,
      totalIncludingVat: vatLine.depositGrossAmountForVatItem,
      type: InvoiceLineType.Deposit,
      vatRateId: vatLine.vatRateId,
      vatRatePercent: vatLine.percentage / 100,
    }),
  )

  return depositLines
}
